import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Users, 
  ArrowLeft, 
  Heart,
  MessageCircle,
  Send,
  Lock,
  Trash2,
  MoreHorizontal,
  Shield,
  ShieldOff,
  Ban,
  UserCheck,
  Settings2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { FounderBadge } from '../components/FounderBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GroupDetail() {
  const { groupId } = useParams();
  const { user, isActiveMember, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [moderators, setModerators] = useState([]);
  const [bannedMembers, setBannedMembers] = useState([]);
  const [groupCreator, setGroupCreator] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isActiveMember) {
      navigate('/membership');
      return;
    }
    fetchGroup();
    fetchPosts();
  }, [groupId, isActiveMember, authLoading]);

  const fetchGroup = async () => {
    try {
      const response = await fetch(`${API}/community/groups/${groupId}`, { credentials: 'include' });
      if (response.ok) {
        setGroup(await response.json());
      } else {
        navigate('/community');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/community/posts?group_id=${groupId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim()) return;
    
    setPosting(true);
    try {
      const response = await fetch(`${API}/community/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newPost, group_id: groupId })
      });
      
      if (response.ok) {
        setNewPost('');
        toast.success('Posted to group!');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  const handleJoinGroup = async () => {
    try {
      const response = await fetch(`${API}/community/groups/${groupId}/join`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Joined group!');
        fetchGroup();
      }
    } catch (error) {
      toast.error('Failed to join');
    }
  };

  const handleLeaveGroup = async () => {
    try {
      const response = await fetch(`${API}/community/groups/${groupId}/leave`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Left group');
        fetchGroup();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to leave');
    }
  };

  const handleLike = async (postId) => {
    try {
      await fetch(`${API}/community/posts/${postId}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      fetchPosts();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      const response = await fetch(`${API}/community/posts/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Post deleted');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const response = await fetch(`${API}/community/groups/${groupId}/members`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || []);
        setModerators(data.moderators || []);
        setBannedMembers(data.banned_members || []);
        setGroupCreator(data.created_by);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handlePromoteModerator = async (memberId) => {
    try {
      const response = await fetch(`${API}/community/groups/${groupId}/moderators/${memberId}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('User promoted to moderator');
        fetchMembers();
        fetchGroup();
      }
    } catch (error) {
      toast.error('Failed to promote user');
    }
  };

  const handleDemoteModerator = async (memberId) => {
    try {
      const response = await fetch(`${API}/community/groups/${groupId}/moderators/${memberId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('User demoted from moderator');
        fetchMembers();
        fetchGroup();
      }
    } catch (error) {
      toast.error('Failed to demote user');
    }
  };

  const handleBanMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to ban this member?')) return;
    try {
      const response = await fetch(`${API}/community/groups/${groupId}/ban/${memberId}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Member banned from group');
        fetchMembers();
        fetchGroup();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to ban member');
      }
    } catch (error) {
      toast.error('Failed to ban member');
    }
  };

  const handleUnbanMember = async (memberId) => {
    try {
      const response = await fetch(`${API}/community/groups/${groupId}/ban/${memberId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Member unbanned');
        fetchMembers();
      }
    } catch (error) {
      toast.error('Failed to unban member');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  if (!group) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A]"></div>
      </div>
    );
  }

  const isMember = group.members?.includes(user?.user_id);
  const isCreator = group.created_by === user?.user_id;
  const isModerator = group.moderators?.includes(user?.user_id);
  const canModerate = isCreator || isModerator || user?.role === 'admin';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="group-detail-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3D4F7A] to-[#2A3755] py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl">
          <Link to="/community" className="inline-flex items-center gap-2 text-slate-200 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Community
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">{group.name}</h1>
                {isCreator && (
                  <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-300 rounded-full">Owner</span>
                )}
                {isModerator && !isCreator && (
                  <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-300 rounded-full">Moderator</span>
                )}
              </div>
              <p className="text-slate-200 mt-1">{group.member_count} members</p>
            </div>
            <div className="flex items-center gap-2">
              {canModerate && (
                <Dialog open={showMembers} onOpenChange={(open) => {
                  setShowMembers(open);
                  if (open) fetchMembers();
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                      data-testid="manage-members-btn"
                    >
                      <Settings2 className="w-4 h-4 mr-2" />
                      Manage
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[80vh] overflow-hidden">
                    <DialogHeader>
                      <DialogTitle>Manage Group Members</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[60vh]">
                      {loadingMembers ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3D4F7A] mx-auto"></div>
                        </div>
                      ) : (
                        <>
                          {/* Members List */}
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Members ({members.length})</p>
                            {members.map((member) => {
                              const isMemberMod = moderators.includes(member.user_id);
                              const isMemberCreator = member.user_id === groupCreator;
                              const isSelf = member.user_id === user?.user_id;
                              
                              return (
                                <div key={member.user_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                                  <div className="flex items-center gap-3">
                                    <Avatar className="w-10 h-10">
                                      <AvatarImage src={member.picture} />
                                      <AvatarFallback className="bg-[#3D4F7A] text-white">
                                        {member.name?.charAt(0)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium text-slate-900 text-sm">{member.name} <FounderBadge tier={member.membership_tier} size={14} /></p>
                                      <div className="flex items-center gap-1">
                                        {isMemberCreator && (
                                          <span className="text-xs text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded">Owner</span>
                                        )}
                                        {isMemberMod && !isMemberCreator && (
                                          <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">Mod</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {!isSelf && !isMemberCreator && (isCreator || user?.role === 'admin') && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                          <MoreHorizontal className="w-4 h-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {isMemberMod ? (
                                          <DropdownMenuItem onClick={() => handleDemoteModerator(member.user_id)}>
                                            <ShieldOff className="w-4 h-4 mr-2" />
                                            Remove Moderator
                                          </DropdownMenuItem>
                                        ) : (
                                          <DropdownMenuItem onClick={() => handlePromoteModerator(member.user_id)}>
                                            <Shield className="w-4 h-4 mr-2" />
                                            Make Moderator
                                          </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                          onClick={() => handleBanMember(member.user_id)}
                                          className="text-red-600"
                                        >
                                          <Ban className="w-4 h-4 mr-2" />
                                          Ban from Group
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Banned Members */}
                          {bannedMembers.length > 0 && (
                            <div className="mt-6 space-y-2">
                              <p className="text-sm font-medium text-red-500 uppercase tracking-wide">Banned ({bannedMembers.length})</p>
                              {bannedMembers.map((bannedId) => (
                                <div key={bannedId} className="flex items-center justify-between p-2 rounded-lg bg-red-50">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-red-200 flex items-center justify-center">
                                      <Ban className="w-4 h-4 text-red-600" />
                                    </div>
                                    <span className="text-sm text-red-700">User ID: {bannedId.slice(0, 12)}...</span>
                                  </div>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => handleUnbanMember(bannedId)}
                                    className="text-green-600 border-green-300 hover:bg-green-50"
                                  >
                                    <UserCheck className="w-4 h-4 mr-1" />
                                    Unban
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              
              {isMember ? (
                !isCreator && (
                  <Button variant="outline" onClick={handleLeaveGroup} className="border-white text-white hover:bg-white/10">
                    Leave Group
                  </Button>
                )
              ) : (
                <Button onClick={handleJoinGroup} className="bg-white text-[#3D4F7A] hover:bg-slate-100">
                  Join Group
                </Button>
              )}
            </div>
          </div>
          <p className="text-slate-300 mt-4">{group.description}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl py-8">
        {isMember ? (
          <>
            {/* Create Post */}
            <Card className="border border-slate-200 shadow-sm mb-6">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Avatar>
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#3D4F7A] text-white">{user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <Textarea 
                      placeholder="Share with this group..."
                      value={newPost}
                      onChange={(e) => setNewPost(e.target.value)}
                      className="resize-none min-h-[80px] mb-3"
                    />
                    <div className="flex justify-end">
                      <Button 
                        onClick={handleCreatePost}
                        disabled={!newPost.trim() || posting}
                        className="bg-[#3D4F7A] hover:bg-[#2A3755]"
                      >
                        {posting ? 'Posting...' : 'Post'}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Posts */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A] mx-auto"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No posts in this group yet. Start the conversation!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <Card key={post.post_id} className="border border-slate-200 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Avatar>
                            <AvatarImage src={post.author_picture} />
                            <AvatarFallback className="bg-[#3D4F7A] text-white">{post.author_name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-slate-900">{post.author_name} <FounderBadge tier={post.author_tier} /></p>
                            <p className="text-xs text-slate-500">{formatDate(post.created_at)}</p>
                          </div>
                        </div>
                        {post.author_id === user?.user_id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDeletePost(post.post_id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      <p className="mt-4 text-slate-700 whitespace-pre-wrap">{post.content}</p>
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-6">
                        <button 
                          onClick={() => handleLike(post.post_id)}
                          className={`flex items-center gap-2 text-sm transition-colors ${
                            post.likes?.includes(user?.user_id) ? 'text-red-500' : 'text-slate-500 hover:text-red-500'
                          }`}
                        >
                          <Heart className={`w-5 h-5 ${post.likes?.includes(user?.user_id) ? 'fill-current' : ''}`} />
                          {post.likes?.length || 0}
                        </button>
                        <span className="flex items-center gap-2 text-sm text-slate-500">
                          <MessageCircle className="w-5 h-5" />
                          {post.comments_count || 0}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <Lock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Join to Participate</h2>
            <p className="text-slate-600 mb-6">You need to join this group to see posts and participate.</p>
            <Button onClick={handleJoinGroup} className="bg-[#3D4F7A] hover:bg-[#2A3755]">
              Join Group
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
